import React, {useEffect, useState, useRef, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {Box, CircularProgress, Typography, Button} from '@mui/material';
import NavBar from '../../pieces/navbar/navbar';
import axios from 'axios';
import {REPORTING_API_BASE, reportingHeaders} from '../../utils/reportingApi';
import fetchHierarchyCompanyIds from '../../utils/companyHierarchy';
import {
  PivotViewComponent,
  Inject as PivotInject,
  ExcelExport
} from '@syncfusion/ej2-react-pivotview';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import {
  AccumulationChartComponent,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
  AccumulationLegend,
  PieSeries,
  AccumulationDataLabel,
  AccumulationTooltip,
  AccumulationSelection,
  Selection,
  ChartAnnotation,
  AccumulationAnnotation,
  AccumulationAnnotationsDirective,
  AccumulationAnnotationDirective,
  Inject
} from '@syncfusion/ej2-react-charts';
import '@syncfusion/ej2-base/styles/material.css';
//import '@syncfusion/ej2-pivotview/styles/material.css';
import '@syncfusion/ej2-react-pivotview/styles/material.css'; 
//import '@syncfusion/ej2-react-charts/styles/material.css'; 
import '@syncfusion/ej2-base/styles/material.css';
import '@syncfusion/ej2-buttons/styles/material.css';
import '@syncfusion/ej2-popups/styles/material.css';
import '@syncfusion/ej2-dropdowns/styles/material.css';



// Month order mapping for chronological sorting
const monthOrder = {
  'January': 1, 'February': 2, 'March': 3, 'April': 4, 'May': 5, 'June': 6,
  'July': 7, 'August': 8, 'September': 9, 'October': 10, 'November': 11, 'December': 12
};

// Path and query for contractor jobs list (cell links use same origin + this path)
const CONTRACTOR_JOBS_LIST_QUERY = 'moduleName=permit&submodule=contractor_jobs&items=50&page=1&view=all';

// Inspection Status definitions with ID, statusKey, and statusNameMapping
const STATUS_DEFINITIONS = [
  { id: 1, statusKey: 1, statusName: '01.New', statusNameMapping: 'New' },
  { id: 2, statusKey: 2, statusName: '02.Pending', statusNameMapping: 'Pending' },
  { id: 3, statusKey: 3, statusName: '03.Sched', statusNameMapping: 'Scheduled' },
  { id: 4, statusKey: 4, statusName: '04.Re-Sched', statusNameMapping: 'Rescheduled' },
  { id: 5, statusKey: 5, statusName: '05.Future Date', statusNameMapping: 'Future Date' },
  { id: 6, statusKey: 6, statusName: '06.Hold', statusNameMapping: 'Hold' },
  { id: 7, statusKey: 7, statusName: '07.Mail Certified Letter', statusNameMapping: 'Mail Certified Letter' },
  { id: 8, statusKey: 8, statusName: '08.Mail Certified Letter 2', statusNameMapping: 'Mail Certified Letter 2' },
  { id: 9, statusKey: 9, statusName: '09.Mail Certified Letter 3', statusNameMapping: 'Mail Certified Letter 3' }, 
  { id: 10, statusKey: 10, statusName: '10.Needs Attention', statusNameMapping: 'Needs Attention' },
  { id: 11, statusKey: 11, statusName: '11.Pending City Response', statusNameMapping: 'Pending City Response' },
  { id: 12, statusKey: 12, statusName: '12.Completed', statusNameMapping: 'Completed' },
  { id: 13, statusKey: 13, statusName: '13.Failed', statusNameMapping: 'Failed' }, 
  { id: 14, statusKey: 14, statusName: '14.No Result', statusNameMapping: 'No Result' },
  { id: 15, statusKey: 15, statusName: '15.Customer Declined', statusNameMapping: 'Customer Declined' },
  { id: 16, statusKey: 16, statusName: '16.Cancelled', statusNameMapping: 'Cancelled' },
];

// Helper function to fill missing combinations
const fillMissingCombinations = (data) => {
  if (!data || data.length === 0) return data;

  // Get all unique months and companies from the data
  const uniqueMonths = [...new Set(data.map(item => item.monthName).filter(Boolean))];
  
  // Use a Map to deduplicate companies by companyId
  const companyMap = new Map();
  data.forEach(item => {
    if (item.companyId && item.companyName) {
      companyMap.set(item.companyId, {
        companyId: item.companyId,
        companyName: item.companyName
      });
    }
  });
  const uniqueCompanies = Array.from(companyMap.values());
  
  // Create a map of existing combinations for quick lookup
  const existingCombinations = new Set();
  data.forEach(item => {
    const key = `${item.monthName}_${item.companyId}_${item.statusKey}`;
    existingCombinations.add(key);
  });

  // Create missing records
  const missingRecords = [];
  
  uniqueMonths.forEach(monthName => {
    uniqueCompanies.forEach(company => {
      STATUS_DEFINITIONS.forEach(status => {
        const key = `${monthName}_${company.companyId}_${status.statusKey}`;
        if (!existingCombinations.has(key)) {
          // Get month number from monthName (e.g., "01. January" or "1. January" -> 1)
          const monthMatch = monthName.match(/^(\d+)\./);
          const month = monthMatch ? parseInt(monthMatch[1], 10) : 0;
          
          missingRecords.push({
            month: month,
            monthName: monthName,
            companyId: company.companyId,
            companyName: company.companyName,
            statusKey: status.statusKey,
            statusName: status.statusName,
            count: 0
          });
        }
      });
    });
  });

  return [...data, ...missingRecords];
};

const InspectionAnalytics = () => {
  const company = useSelector(state => state.company);
  const user = useSelector(state => state.user);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobStatusData, setJobStatusData] = useState([]);
  const [pieChartData, setPieChartData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  //const [companies, setCompanies] = useState([]);

  const pivotRef = useRef(null);

  // Get company ID from user or company state
  const companyId = user && user.companyId ? user.companyId : (company && company._id ? company._id : 'all');
  
  //console.log('companyId', companyId);
  
  useEffect(() => {


    const fetchData = async () => {
      try {
  
        const compList = user?.userCompanies ?? [];
        const rootIds = compList.length > 0
            ? compList.map(company => company.companyId)
            : [user?.companyId].filter(Boolean);

        // Nothing resolvable yet (the user record is still loading). Bail out
        // rather than querying with an empty list, which the API reads as
        // "every company".
        if (rootIds.length === 0) {
            return;
        }

        // An Enterprise/Primary account holds no jobs itself -- its members do.
        const companies = await fetchHierarchyCompanyIds(rootIds);

        //console.log('compList', compList);
        //console.log('companies', companies);

        const url = `${REPORTING_API_BASE}/FactInspection/by-company`;
      
        const response = await axios.post(
          url,
          {companies, year: selectedYear},  // Request body (data)
          {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
              ...(await reportingHeaders()),
            }
          }
        ); 

        //console.log('response', response.data);

        // Fill missing combinations with zero counts
        const filledData = fillMissingCombinations(response.data);
        setJobStatusData(filledData);
        setLoading(false);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData().catch(console.error);

  }, [user, selectedYear]);

  // Prepare company dropdown data
  const availableCompanies = useMemo(() => {
    if (!user?.userCompanies || user.userCompanies.length === 0) return [];
    return user.userCompanies.map(company => ({
      text: company.companyName || '',
      value: company.companyId || ''
    }));
  }, [user]);

  // Set default company to first available if not set
  useEffect(() => {
    if (availableCompanies.length > 0 && !selectedCompany) {
      setSelectedCompany(availableCompanies[0].value);
    }
  }, [availableCompanies, selectedCompany]);

  // Extract available months from data
  const availableMonths = useMemo(() => {
    if (!jobStatusData || jobStatusData.length === 0) return [];
    const months = [...new Set(jobStatusData.map(item => item.monthName).filter(Boolean))];
    return months
      .map(month => {
        // Remove numeric prefix like "01.", "02.", "1.", "2." etc. from display text
        const cleanMonthName = month.replace(/^\d+\.\s*/, '');
        return { 
          text: cleanMonthName, 
          value: month, // Keep original value for filtering
          sortOrder: monthOrder[cleanMonthName] || 999 // Add sort order for sorting
        };
      })
      .sort((a, b) => a.sortOrder - b.sortOrder) // Sort by chronological order
      .map(({ sortOrder, ...item }) => item); // Remove sortOrder from final result
  }, [jobStatusData]);

  // Set default month to current month if not set
  useEffect(() => {
    if (availableMonths.length > 0 && !selectedMonth) {
      const currentDate = new Date();
      const currentMonthName = currentDate.toLocaleString('default', { month: 'long' }); // e.g., "January"
      
      // Find the month value that matches the current month
      const currentMonthOption = availableMonths.find(month => {
        const cleanMonthName = month.text; // Already cleaned (e.g., "January")
        return cleanMonthName === currentMonthName;
      });
      
      // If current month is found, use it; otherwise use the first available
      setSelectedMonth(currentMonthOption ? currentMonthOption.value : availableMonths[0].value);
    }
  }, [availableMonths, selectedMonth]);

  // Calculate pie chart data based on selected month and company
  useEffect(() => {
    if (!jobStatusData || jobStatusData.length === 0 || !selectedMonth) {
      setPieChartData([]);
      return;
    }

    // Filter data by selected month and company (if company is selected)
    // console.log('=== PIE CHART FILTERING DEBUG ===');
    // console.log('selectedMonth:', selectedMonth);
    // console.log('selectedCompany:', selectedCompany, 'type:', typeof selectedCompany);
    // console.log('Total jobStatusData items:', jobStatusData.length);
    
    // if (jobStatusData.length > 0) {
    //   console.log('Sample item:', jobStatusData[0]);
    //   console.log('Sample item companyId:', jobStatusData[0].companyId, 'type:', typeof jobStatusData[0].companyId);
    //   console.log('Sample item monthName:', jobStatusData[0].monthName);
    //   console.log('Sample item count:', jobStatusData[0].count);
    // }
    
    const filteredData = jobStatusData.filter(item => {
      // Compare monthName - exact match first, then try without prefix
      const itemMonthName = item.monthName || '';
      const selectedMonthClean = selectedMonth || '';
      const monthMatch = itemMonthName === selectedMonthClean || 
                        itemMonthName.replace(/^\d+\.\s*/, '') === selectedMonthClean.replace(/^\d+\.\s*/, '');
      
      // Compare companyId - handle both string and number types
      let companyMatch = true;
      if (selectedCompany) {
        const itemCompanyId = item.companyId;
        const selectedCompanyId = selectedCompany;
        // Try multiple comparison methods to handle type mismatches
        companyMatch = String(itemCompanyId) === String(selectedCompanyId) ||
                      Number(itemCompanyId) === Number(selectedCompanyId) ||
                      itemCompanyId == selectedCompanyId; // Loose equality for type coercion
        
        // Debug individual item matching
        // if (!companyMatch && itemMonthName === selectedMonthClean) {
        //   console.log('Company mismatch - item.companyId:', itemCompanyId, 'selectedCompany:', selectedCompanyId, 
        //              'String match:', String(itemCompanyId) === String(selectedCompanyId),
        //              'Number match:', Number(itemCompanyId) === Number(selectedCompanyId),
        //              'Loose match:', itemCompanyId == selectedCompanyId);
        // }
      }
      
      return monthMatch && companyMatch;
    });
    
    //console.log('Filtered data count:', filteredData.length);
    if (filteredData.length > 0) {
      //console.log('Sample filtered items (first 3):', filteredData.slice(0, 3));
    } else {
      //console.log('NO DATA MATCHED - checking why...');
      // Check month matches
      const monthMatches = jobStatusData.filter(item => {
        const itemMonthName = item.monthName || '';
        const selectedMonthClean = selectedMonth || '';
        return itemMonthName === selectedMonthClean || 
               itemMonthName.replace(/^\d+\.\s*/, '') === selectedMonthClean.replace(/^\d+\.\s*/, '');
      });
      //console.log('Items matching month:', monthMatches.length);
      
      // Check company matches
      if (selectedCompany) {
        const companyMatches = jobStatusData.filter(item => {
          const itemCompanyId = item.companyId;
          const selectedCompanyId = selectedCompany;
          return String(itemCompanyId) === String(selectedCompanyId) ||
                 Number(itemCompanyId) === Number(selectedCompanyId) ||
                 itemCompanyId == selectedCompanyId;
        });
        //console.log('Items matching company:', companyMatches.length);
        // if (companyMatches.length > 0) {
        //   console.log('Sample company match:', companyMatches[0]);
        // }
      }
    }
    
    //console.log('Filtered data length:', filteredData.length);
    
    const statusMap = {};
    filteredData.forEach(item => {
      const statusName = item.statusName || '';
      // Remove numeric prefix like "1.", "2.", "12." etc. if present
      const cleanStatusName = statusName.replace(/^\d+\./, '');
      const count = item.count || 0;
      
      if (statusMap[cleanStatusName]) {
        statusMap[cleanStatusName] += count;
      } else {
        statusMap[cleanStatusName] = count;
      }
    });
    
    // console.log('Status map:', statusMap);
    // console.log('Status map keys:', Object.keys(statusMap));
    
    // Convert to array format for pie chart, filtering out statuses with count = 0
    // Sort by statusKey to match PivotViewComponent column header order
    const chartData = Object.keys(statusMap)
      .filter(statusName => statusMap[statusName] > 0) // Filter out statuses with count = 0
      .map(statusName => {
        // Find the statusKey for this statusName from STATUS_DEFINITIONS
        const statusDef = STATUS_DEFINITIONS.find(def => def.statusNameMapping === statusName);
        const statusKey = statusDef ? statusDef.statusKey : 999; // Use 999 as fallback for unknown statuses
        return {
          x: statusName,
          y: statusMap[statusName],
          statusKey: statusKey
        };
      })
      .sort((a, b) => a.statusKey - b.statusKey) // Sort by statusKey to match column header order
      .map(({ statusKey, ...item }) => item); // Remove statusKey from final data
    
    // Calculate percentages
    const totalSum = chartData.reduce((sum, item) => sum + item.y, 0);
    const chartDataWithPercentages = chartData.map((item) => {
      const percentage = totalSum > 0 ? parseFloat(((item.y / totalSum) * 100).toFixed(2)) : 0;
      const labelText = `${percentage.toFixed(2)}%`;
      return {
        ...item,
        y: percentage,
        label: labelText,
        text: labelText
      };
    });
    
    //console.log('chartDataWithPercentages', chartDataWithPercentages);
    
    setPieChartData(chartDataWithPercentages);
  }, [jobStatusData, selectedMonth, selectedCompany]);

  // Transform data to remove numeric prefixes from monthName and sort by month order
  const transformedJobStatusData = useMemo(() => {
    if (!jobStatusData || jobStatusData.length === 0) return [];
    
    // Transform existing records - clean up month names and add sort order
    // Keep statusName with numeric prefix for proper sorting by statusKey (like permitJobAnalytics.js)
    const transformedRecords = jobStatusData.map(item => {
      const monthName = item.monthName || '';
      const cleanMonthName = monthName.replace(/^\d+\.\s*/, ''); // Remove prefix for display
      // Keep statusName with prefix (e.g., "01.New", "02.Pending") so columns sort by statusKey
      const statusName = item.statusName || '';
      return {
        ...item,
        monthName: cleanMonthName,
        statusName: statusName, // Keep original statusName with numeric prefix for sorting
        monthSortOrder: monthOrder[cleanMonthName] || 999 // Add sort order for sorting
      };
    });
    
    // Sort by company name, then by month order
    return transformedRecords.sort((a, b) => {
      // First sort by company name
      const companyCompare = (a.companyName || '').localeCompare(b.companyName || '');
      if (companyCompare !== 0) return companyCompare;
      // Then sort by month order
      return (a.monthSortOrder || 999) - (b.monthSortOrder || 999);
    });
  }, [jobStatusData]);

  // Generate years from 2020 to 2030
  const availableYears = useMemo(() => {
    const years = [];
    for (let year = 2030; year >= 2020; year--) {
      years.push({ text: year.toString(), value: year });
    }
    return years;
  }, []);

  // // Set default year to 2024 (or most recent available in data)
  // useEffect(() => {
  //   if (!selectedYear && availableYears.length > 0) {
  //     // Check if data has years and set to most recent, otherwise default to 2024
  //     if (jobStatusData && jobStatusData.length > 0) {
  //       const dataYears = [...new Set(jobStatusData.map(item => item.year).filter(Boolean))];
  //       if (dataYears.length > 0) {
  //         const mostRecent = Math.max(...dataYears);
  //         setSelectedYear(mostRecent);
  //       } else {
  //         setSelectedYear(2024);
  //       }
  //     } else {
  //       setSelectedYear(2024);
  //     }
  //   }
  // }, [availableYears, selectedYear, jobStatusData]);

  // Filter data by selected year
  // const filteredJobStatusData = useMemo(() => {
  //   if (!selectedYear || !jobStatusData || jobStatusData.length === 0) return jobStatusData;
  //   return jobStatusData.filter(item => item.year === selectedYear);
  // }, [jobStatusData, selectedYear]);


  // Load function for the pie chart
  const loadChart = (args) => {
    // Chart load customization if needed
  };

  // Prevent label truncation and ensure all labels are visible by manipulating SVG text elements
  useEffect(() => {
    if (!pieChartData || pieChartData.length === 0) return;

    const ensureAllLabelsVisible = () => {
      const chartElement = document.getElementById('pie-chart2');
      if (!chartElement) return;

      // Find all data label groups and ensure they are visible
      const dataLabelGroups = chartElement.querySelectorAll('[id*="datalabel"], [class*="datalabel"], g[id*="label"]');
      dataLabelGroups.forEach((group) => {
        group.style.display = 'block';
        group.style.visibility = 'visible';
        group.style.opacity = '1';
        group.removeAttribute('display');
      });

      // Find all data label text elements and fix blank ones
      const dataLabelTexts = chartElement.querySelectorAll('[id*="datalabel"] text, [class*="datalabel"] text, g[id*="Series"] text');
      let labelIndex = 0;
      
      dataLabelTexts.forEach((textEl) => {
        // Force visibility
        textEl.style.display = 'block';
        textEl.style.visibility = 'visible';
        textEl.style.opacity = '1';
        textEl.removeAttribute('display');
        textEl.style.textOverflow = 'clip';
        textEl.style.overflow = 'visible';
        textEl.removeAttribute('textLength');
        textEl.removeAttribute('lengthAdjust');
        
        const text = (textEl.textContent || '').trim();
        
        // Fix blank or invalid labels
        if ((!text || text === '' || text.length <= 1) && pieChartData && pieChartData.length > 0) {
          // Try to assign a label from the data based on index
          if (labelIndex < pieChartData.length && pieChartData[labelIndex].label) {
            textEl.textContent = pieChartData[labelIndex].label;
          }
          labelIndex++;
        } else if (text.includes('...')) {
          // Fix truncated labels
          const matchingData = pieChartData.find(d => d.label && d.label.startsWith(text.replace('...', '')));
          if (matchingData && matchingData.label) {
            textEl.textContent = matchingData.label;
          }
        } else if (text.length <= 2 && !text.includes('%') && pieChartData && pieChartData.length > 0) {
          // Single digit - might be truncated percentage
          const matchingData = pieChartData.find(d => d.label && d.label.startsWith(text));
          if (matchingData && matchingData.label) {
            textEl.textContent = matchingData.label;
          }
        }
      });
      
      // Also check all text elements
      const allTextElements = chartElement.querySelectorAll('text, tspan');
      allTextElements.forEach((textEl) => {
        textEl.style.display = 'block';
        textEl.style.visibility = 'visible';
        textEl.style.opacity = '1';
        if (textEl.style.display === 'none') {
          textEl.style.display = 'block';
        }
      });

      // Also check for hidden label groups and paths
      const labelGroups = chartElement.querySelectorAll('[class*="datalabel"], [class*="label"], g[class*="label"]');
      labelGroups.forEach((group) => {
        if (group.style.display === 'none' || group.style.visibility === 'hidden') {
          group.style.display = 'block';
          group.style.visibility = 'visible';
        }
        group.style.opacity = '1';
      });

      // Check for any elements with display:none in computed styles
      const allElements = chartElement.querySelectorAll('*');
      allElements.forEach((el) => {
        if (el.tagName === 'text' || el.tagName === 'tspan' || el.classList.toString().includes('label') || el.classList.toString().includes('datalabel')) {
          const computedStyle = window.getComputedStyle(el);
          if (computedStyle.display === 'none' || computedStyle.visibility === 'hidden') {
            el.style.display = 'block';
            el.style.visibility = 'visible';
          }
        }
      });
    };

    // Run immediately and after delays to catch dynamic updates
    ensureAllLabelsVisible();
    const timeout1 = setTimeout(ensureAllLabelsVisible, 100);
    const timeout2 = setTimeout(ensureAllLabelsVisible, 500);
    const timeout3 = setTimeout(ensureAllLabelsVisible, 1000);

    // Use MutationObserver to catch when labels are added or modified
    const chartElement = document.getElementById('pie-chart2');
    if (chartElement) {
      const observer = new MutationObserver(() => {
        ensureAllLabelsVisible();
      });

      observer.observe(chartElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'display', 'visibility', 'opacity']
      });

      return () => {
        clearTimeout(timeout1);
        clearTimeout(timeout2);
        clearTimeout(timeout3);
        observer.disconnect();
      };
    }

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
    };
  }, [pieChartData]);

  const onHeaderCellInfo = (args) => {
    const col = args.cell?.column;
    if (!col) return;
  
    if (col.index === 0) {
      // first column: company + month
      col.width = 200;
    } else {
      // metric columns - ensure consistent width
      col.width = 50;
    }

    if (args.node) {
      args.node.style.width = col.width + 'px';
      args.node.style.minWidth = col.width + 'px';
      args.node.style.maxWidth = col.width + 'px';
    }
  };


  // Excel export complete handler
  const excelExportComplete = (args) => {
    // Optional: Handle export completion
  };

  // Function to trigger Excel export
  const exportToExcel = () => {
    if (pivotRef.current) {
      pivotRef.current.excelExport();
    }
  };

  // Add percentage to each cell in the pivot table
  useEffect(() => {
    if (!jobStatusData || jobStatusData.length === 0) return;

    // Map company name -> companyId for building cell links (normalize key by trim for lookup)
    const companyNameToId = {};
    const companyIdToName = {}; // id -> name (pivot may show id in row header)
    jobStatusData.forEach((item) => {
      const name = item.companyName ?? item.CompanyName;
      const id = item.companyId ?? item.CompanyId;
      if (name != null && id != null) {
        const key = String(name).trim();
        companyNameToId[key] = id;
        companyIdToName[String(id)] = key;
        companyIdToName[id] = key;
      }
    });

    const cleanupHeaders = () => {
      const pivotView = document.getElementById('FactInspectionPivot');
      if (!pivotView) return;

      // Find all header cells - target column headers specifically
      const columnHeaders = pivotView.querySelectorAll('.e-columnsheader .e-headercell, .e-columnsheader .e-headercelldiv, .e-columnsheader th, .e-columnsheader .e-headercell span, .e-columnsheader .e-headercelldiv span');
      columnHeaders.forEach(header => {
        if (!header || !header.textContent) return;
        
        let text = (header.textContent || '').trim();
        const originalText = text;
        
        // Remove patterns like "01.", "02.", etc. from the beginning
        text = text.replace(/^\d{2}\.\s*/i, '');
        
        if (text !== originalText && text.length > 0) {
          header.textContent = text;
        }
      });
    };

    // Add percentage to each cell based on row's custom total (excluding certain statuses)
    const addPercentagesToCells = () => {
      const pivotView = document.getElementById('FactInspectionPivot');
      if (!pivotView) return;

      // Statuses to exclude from total calculation
      const excludedStatuses = ['Cancelled', 'Grand Total'];

      // Get header row to identify column indices to exclude and to get statusName per column
      const headerRow = pivotView.querySelector('.e-gridheader table thead tr:last-child');
      const headerCells = headerRow ? headerRow.querySelectorAll('th') : [];
      const excludedColumnIndices = new Set();
      
      if (headerRow) {
        headerCells.forEach((cell, index) => {
          const headerText = (cell.textContent || '').trim();
          // Check if this header matches any excluded status
          if (excludedStatuses.some(status => headerText.includes(status))) {
            excludedColumnIndices.add(index);
          }
        });
      }

      const bodyTable = pivotView.querySelector('.e-content table, .e-gridcontent table');
      if (!bodyTable) return;

      const bodyRows = Array.from(bodyTable.querySelectorAll('tbody tr'));
      const monthNames = Object.keys(monthOrder);
      // Pivot has 2 row dimensions (companyName, monthName) so first 2 columns are row headers
      const ROW_HEADER_COLUMN_COUNT = 1;
      const COMPANY_COLUMN_INDEX = 0;
      const MONTH_COLUMN_INDEX = 1;

      bodyRows.forEach((row, rowIndex) => {
        const cells = row.querySelectorAll('td');
        if (!cells || cells.length <= ROW_HEADER_COLUMN_COUNT) return;

        // Month from Month column (index 1); also check column 0 in case pivot layout differs
        let monthForRow = null;
        const cell1 = (cells[MONTH_COLUMN_INDEX]?.textContent || '').trim();
        const cell0 = (cells[COMPANY_COLUMN_INDEX]?.textContent || '').trim();
        
        // Check if this is a total row (has "Total" or "Grand Total" in header cells)
        const isTotalRow = (cell0 && (
          cell0.toLowerCase().includes('total') || 
          cell0.toLowerCase().includes('grand total')
        )) || (cell1 && (
          cell1.toLowerCase().includes('total') || 
          cell1.toLowerCase().includes('grand total')
        ));
        if (cell1) {
          monthForRow = monthNames.find(m => cell1 === m || cell1.endsWith(m) || cell1.includes(m)) || null;
        }
        if (!monthForRow && cell0) {
          monthForRow = monthNames.find(m => cell0 === m || cell0.endsWith(m) || cell0.includes(m)) || null;
        }

        // Company name from parent row's Company column (index 0) when on a month row
        // This companyName will be used for all column cells in this row when creating links
        let currentCompanyName = null;
        if (monthForRow) {
          currentCompanyName = null;
          // Look backwards to find the direct parent company row (has company name but no month)
          // This ensures we get the company name from the correct parent, not from a previous month's row
          for (let p = rowIndex - 1; p >= 0; p--) {
            const prevRow = bodyRows[p];
            if (!prevRow) break;
            const prevCells = prevRow.querySelectorAll('td');
            const prevCompanyCellText = (prevCells[COMPANY_COLUMN_INDEX]?.textContent || '').trim();
            const prevMonthCellText = (prevCells[MONTH_COLUMN_INDEX]?.textContent || '').trim();
            
            // If month column has a month name, this is a month row, not a company row - skip it
            const prevMonthIsMonth = prevMonthCellText && monthNames.some(m => {
              const trimmed = prevMonthCellText.trim();
              return trimmed === m || 
                     trimmed.startsWith(m + ' ') || 
                     trimmed.endsWith(' ' + m) ||
                     (trimmed.length === m.length && trimmed.toLowerCase() === m.toLowerCase());
            });
            
            // Skip if this is a month row (has month in column 1)
            if (prevMonthIsMonth) continue;
            
            // Check if company cell text is actually a month name (should not be treated as company)
            const companyCellIsMonth = prevCompanyCellText && monthNames.some(m => {
              const trimmed = prevCompanyCellText.trim();
              return trimmed === m || 
                     trimmed.startsWith(m + ' ') || 
                     trimmed.endsWith(' ' + m) ||
                     (trimmed.length === m.length && trimmed.toLowerCase() === m.toLowerCase());
            });
            
            // Skip if company cell text is a month name
            if (companyCellIsMonth) continue;
            
            // Check if the company cell text is a valid company name (exists in companyNameToId mapping)
            const isValidCompanyName = prevCompanyCellText && (
              companyNameToId[prevCompanyCellText] != null ||
              companyNameToId[prevCompanyCellText.trim()] != null ||
              Object.keys(companyNameToId).some(name => {
                const nameTrimmed = name.trim();
                const cellTrimmed = prevCompanyCellText.trim();
                return nameTrimmed === cellTrimmed || 
                       nameTrimmed.toLowerCase() === cellTrimmed.toLowerCase() ||
                       (cellTrimmed.length > 5 && nameTrimmed.includes(cellTrimmed)) ||
                       (nameTrimmed.length > 5 && cellTrimmed.includes(nameTrimmed));
              })
            );
            
            // This is a company header row if it has a valid company name and no month
            const isCompanyRow = prevCompanyCellText && isValidCompanyName;
            
            if (isCompanyRow) {
              currentCompanyName = prevCompanyCellText;
              break;
            }
          }
        } else {
          const companyCellText = (cells[COMPANY_COLUMN_INDEX]?.textContent || '').trim();
          // Check if company cell text is actually a month name (use strict matching)
          const companyCellIsMonth = companyCellText && monthNames.some(m => {
            const trimmed = companyCellText.trim();
            return trimmed === m || 
                   trimmed.startsWith(m + ' ') || 
                   trimmed.endsWith(' ' + m) ||
                   (trimmed.length === m.length && trimmed.toLowerCase() === m.toLowerCase());
          });
          // Check if it's a valid company name
          const isValidCompanyName = companyCellText && (
            companyNameToId[companyCellText] != null ||
            companyNameToId[companyCellText.trim()] != null ||
            Object.keys(companyNameToId).some(name => {
              const nameTrimmed = name.trim();
              const cellTrimmed = companyCellText.trim();
              return nameTrimmed === cellTrimmed || 
                     nameTrimmed.toLowerCase() === cellTrimmed.toLowerCase() ||
                     (cellTrimmed.length > 5 && nameTrimmed.includes(cellTrimmed)) ||
                     (nameTrimmed.length > 5 && cellTrimmed.includes(nameTrimmed));
            })
          );
          if (companyCellText && !companyCellIsMonth && isValidCompanyName) {
            currentCompanyName = companyCellText;
          }
        }

        // Calculate custom total excluding specific status columns
        let customTotal = 0;
        cells.forEach((cell, index) => {
          // Skip row header columns, last column (Grand Total), and excluded columns
          if (index < ROW_HEADER_COLUMN_COUNT || index === cells.length - 1) return;
          if (excludedColumnIndices.has(index)) return;

          const cellText = cell.textContent || '';
          const cellMatch = cellText.match(/^[\s]*([\d,]+)/);
          const cellValue = cellMatch ? parseFloat(cellMatch[1].replace(/,/g, '')) : 0;
          if (!isNaN(cellValue)) {
            customTotal += cellValue;
          }
        });

        // Skip if custom total is 0 or invalid
        if (customTotal === 0 || isNaN(customTotal)) return;

        // Look up companyId by exact or partial match (trimmed, case-insensitive)
        let companyIdForRow = null;
        if (currentCompanyName) {
          const key = currentCompanyName.trim();
          companyIdForRow = companyNameToId[key] ?? companyNameToId[currentCompanyName];
          if (companyIdForRow == null) {
            const match = Object.keys(companyNameToId).find(
              (name) => key.includes(name) || name.includes(key)
            );
            if (match) companyIdForRow = companyNameToId[match];
          }
          if (companyIdForRow == null) {
            const keyLower = key.toLowerCase();
            const match = Object.keys(companyNameToId).find(
              (name) => {
                const nameLower = name.toLowerCase();
                return keyLower === nameLower || keyLower.includes(nameLower) || nameLower.includes(keyLower);
              }
            );
            if (match) companyIdForRow = companyNameToId[match];
          }
        }

        // Process each data cell (skip row header columns)
        cells.forEach((cell, index) => {
          if (index < ROW_HEADER_COLUMN_COUNT) return;
          if (excludedColumnIndices.has(index)) return;
          if (cell.querySelector('.cell-percentage')) return;

          const cellText = cell.textContent || '';
          // Skip if cell is empty or just whitespace
          if (!cellText.trim()) return;

          // Extract the numeric value from cell
          const cellMatch = cellText.match(/^[\s]*([\d,]+)/);
          const cellValue = cellMatch ? parseFloat(cellMatch[1].replace(/,/g, '')) : 0;

          // Skip if value is NaN (but allow 0 values for alignment)
          if (isNaN(cellValue)) return;

          // Calculate percentage using custom total (excluding certain statuses)
          const percentage = ((cellValue / customTotal) * 100).toFixed(1);

          // Create percentage span
          const percentSpan = document.createElement('span');
          percentSpan.className = 'cell-percentage';
          percentSpan.style.cssText = 'font-size:12px;color:#666;display:block;';
          percentSpan.textContent = `(${percentage}%)`;

          // Clear cell and add value with percentage
          cell.innerHTML = '';
          const valueSpan = document.createElement('span');
          valueSpan.style.cssText = 'font-size:14px;font-weight:500;';
          valueSpan.textContent = cellValue.toLocaleString();

          // Wrap in clickable link when we have a month for this row (but not for total rows)
          if (monthForRow && !isTotalRow) {
            const link = document.createElement('a');
            const params = new URLSearchParams();
            // currentMonth as date with day=1, month and year from the cell (selectedYear)
            const monthNum = monthOrder[monthForRow];
            const year = selectedYear ?? new Date().getFullYear();
            const currentMonthDate = monthNum != null
              ? `${year}-${String(monthNum).padStart(2, '0')}-01`
              : String(monthForRow);
            params.set('currentMonth', currentMonthDate);
            // companyName from parent company row
            if (currentCompanyName) {
              const companyNameForUrl = companyIdToName[currentCompanyName] ?? companyIdToName[String(currentCompanyName)] ?? currentCompanyName.replace(/\s+/g, ' ').trim();
              if (companyNameForUrl) params.set('companyName', companyNameForUrl);
            }
            // Add statusName from column header
            // Header row may or may not include row header columns, so try both
            let headerCell = headerCells[index];
            // If header row doesn't include row headers, adjust index
            if (!headerCell && index >= ROW_HEADER_COLUMN_COUNT) {
              headerCell = headerCells[index - ROW_HEADER_COLUMN_COUNT];
            }
            const columnHeaderText = (headerCell?.textContent || '').trim();
            // Try exact match first, then partial match
            // Prioritize matches that include numbers (like "Mail Certified Letter 2") to avoid matching "Mail Certified Letter" first
            let statusDef = null;
            if (columnHeaderText) {
              // First try exact match
              statusDef = STATUS_DEFINITIONS.find(
                (d) => columnHeaderText === d.statusNameMapping || columnHeaderText === d.statusName.replace(/^\d+\./, '').trim()
              );
              // If no exact match, try to find the most specific match (prefer matches with numbers)
              if (!statusDef) {
                const matchesWithNumbers = STATUS_DEFINITIONS.filter(
                  (d) =>
                    (columnHeaderText.includes(d.statusNameMapping) && /\d/.test(d.statusNameMapping)) ||
                    (d.statusNameMapping.includes(columnHeaderText) && /\d/.test(d.statusNameMapping))
                );
                if (matchesWithNumbers.length > 0) {
                  statusDef = matchesWithNumbers[0];
                } else {
                  // Fall back to any match
                  statusDef = STATUS_DEFINITIONS.find(
                    (d) =>
                      columnHeaderText.includes(d.statusName) ||
                      columnHeaderText.includes(d.statusNameMapping) ||
                      d.statusName.includes(columnHeaderText) ||
                      d.statusNameMapping.includes(columnHeaderText)
                  );
                }
              }
            }
            if (statusDef) {
              params.set('statusName', statusDef.statusNameMapping);
            } else if (columnHeaderText) {
              params.set('statusName', columnHeaderText);
            }
            // Use + for spaces in query string so company name appears as text (not %20)
            const url = `${window.location.origin}/module?${CONTRACTOR_JOBS_LIST_QUERY}&${params.toString().replace(/%20/g, '+')}`;
            link.href = url;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.style.cssText = 'color:inherit;text-decoration:none;cursor:pointer;';
            link.appendChild(valueSpan);
            link.appendChild(percentSpan);
            link.addEventListener('click', (e) => {
              e.preventDefault();
              window.open(url, '_blank');
            });
            cell.appendChild(link);
          } else {
            cell.appendChild(valueSpan);
            cell.appendChild(percentSpan);
          }
        });
      });
    };

    // Force first column width to 200px (override pivot table component)
    const forceFirstColumnWidth = () => {
      const pivotView = document.getElementById('FactInspectionPivot');
      if (!pivotView) return;

      // Target all possible first column elements
      const firstColumnSelectors = [
        'table thead th:nth-child(1)',
        'table tbody td:nth-child(1)',
        '.e-rowsheader th:nth-child(1)',
        '.e-rowsheader td:nth-child(1)',
        'table tbody td.e-rowsheader:nth-child(1)',
        '.e-gridcell:nth-child(1)',
        '.e-headercell:nth-child(1)',
        '.e-gridheader th:nth-child(1)',
        '.e-content td:nth-child(1)'
      ];

      firstColumnSelectors.forEach(selector => {
        const elements = pivotView.querySelectorAll(selector);
        elements.forEach(el => {
          if (el) {
            el.style.setProperty('width', '200px', 'important');
            el.style.setProperty('min-width', '200px', 'important');
            el.style.setProperty('max-width', '200px', 'important');
          }
        });
      });
    };

    // Run immediately
    cleanupHeaders();
    addPercentagesToCells();
    forceFirstColumnWidth();
    requestAnimationFrame(() => {
      cleanupHeaders();
      addPercentagesToCells();
      forceFirstColumnWidth();
      requestAnimationFrame(() => {
        cleanupHeaders();
        addPercentagesToCells();
        forceFirstColumnWidth();
      });
    });

    // Use MutationObserver to catch dynamic updates
    const observer = new MutationObserver(() => {
      cleanupHeaders();
      addPercentagesToCells();
      forceFirstColumnWidth();
      requestAnimationFrame(() => {
        cleanupHeaders();
        addPercentagesToCells();
        forceFirstColumnWidth();
        requestAnimationFrame(() => {
          cleanupHeaders();
          addPercentagesToCells();
          forceFirstColumnWidth();
        });
      });
    });

    const pivotView = document.getElementById('FactInspectionPivot');
    if (pivotView) {
      observer.observe(pivotView, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true
      });

      return () => {
        observer.disconnect();
      };
    }
  }, [jobStatusData, pieChartData, selectedYear]); // Re-run when year changes for currentMonth date

  return (
    <>
    <style>
      {`
        /* Remove top margin/padding from AccumulationChartComponent */
        #pie-chart2,
        #pie-chart2 .e-accumulationchart,
        #pie-chart2 .e-accumulationchart-container {
          margin-top: 0px !important;
          padding-top: 0px !important;
          margin-left: 0px !important;
          padding-left: 0px !important;
        }
        /* Left align pie chart with padding for labels */
        #pie-chart2 {
          display: flex !important;
          justify-content: flex-start !important;
          align-items: flex-start !important;
          margin: 0 !important;
          padding: 0 10px 0 10px !important;
          overflow: visible !important;
        }
        #pie-chart2 .e-accumulationchart-container {
          display: flex !important;
          justify-content: flex-start !important;
          align-items: flex-start !important;
          margin: 0 auto 0 0 !important;
          padding: 0 !important;
        }
        #pie-chart2 .e-accumulationchart {
          margin: 0 auto 0 0 !important;
          padding: 0 !important;
        }
        #pie-chart2 svg {
          margin: 0 auto 0 0 !important;
          display: block !important;
        }
        /* Prevent label truncation in pie chart data labels */
        #pie-chart2 .e-accumulation-datalabel,
        #pie-chart2 .e-accumulation-datalabel text,
        #pie-chart2 .e-accumulation-datalabel tspan,
        #pie-chart2 .e-datalabel,
        #pie-chart2 .e-datalabel text,
        #pie-chart2 .e-datalabel tspan,
        #pie-chart2 text,
        #pie-chart2 tspan {
          text-overflow: clip !important;
          overflow: visible !important;
          white-space: nowrap !important;
          text-anchor: start !important;
          max-width: none !important;
          width: auto !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
        /* Prevent any text truncation in SVG */
        #pie-chart2 svg text,
        #pie-chart2 svg tspan {
          text-overflow: clip !important;
          overflow: visible !important;
          white-space: nowrap !important;
          max-width: none !important;
          width: auto !important;
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
        }
        /* Ensure all label groups are visible and on top */
        #pie-chart2 [class*="datalabel"],
        #pie-chart2 [class*="label"] {
          display: block !important;
          visibility: visible !important;
          opacity: 1 !important;
          z-index: 9999 !important;
          position: relative !important;
        }
        /* Ensure pie chart SVG and labels are on top of all controls */
        #pie-chart2 svg {
          overflow: visible !important;
          z-index: 9999 !important;
        }
        #pie-chart2 svg text,
        #pie-chart2 svg g[class*="label"],
        #pie-chart2 svg g[class*="datalabel"] {
          z-index: 10000 !important;
        }
        /* Pie chart tooltip on top of pie chart */
        #pie-chart2_tooltip,
        #pie-chart2_svg_tooltip,
        .e-tooltip-wrap,
        .e-accumulationchart-tooltip,
        .e-chart-tooltip,
        [id*="tooltip"] {
          z-index: 99999 !important;
        }
        /* Prevent label truncation in pie chart legend */
        #pie-chart2 .e-legend-text {
          text-overflow: clip !important;
          overflow: visible !important;
          white-space: normal !important;
          word-wrap: break-word !important;
        }
        #pie-chart2 .e-legend-item {
          overflow: visible !important;
        }
        /* Move legend to the right to prevent label truncation */
        #pie-chart2 .e-legend {
          margin-left: 60px !important;
          padding-left: 0px !important;
        }
        /* Ensure legend text has enough space */
        #pie-chart2 .e-legend-text {
          max-width: none !important;
          width: auto !important;
        }
        #FactInspectionPivot table caption,
        #FactInspectionPivot .e-gridheader table caption,
        #FactInspectionPivot .e-pivotview table caption {
          display: none !important;
          visibility: hidden !important;
          height: 0 !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        /* Keep column headers in one row - prevent diagonal/rotated display */
        #FactInspectionPivot .e-headercell,
        #FactInspectionPivot .e-headercelldiv,
        #FactInspectionPivot .e-valueheader .e-headercell,
        #FactInspectionPivot .e-valueheader .e-headercelldiv,
        #FactInspectionPivot .e-columnsheader .e-headercell,
        #FactInspectionPivot .e-columnsheader .e-headercelldiv {
          transform: none !important;
          writing-mode: horizontal-tb !important;
          text-orientation: mixed !important;
          white-space: normal !important;
          text-align: center !important;
          vertical-align: middle !important;
        }
        /* Remove bold from column headers */
        #FactInspectionPivot .e-columnsheader .e-headercell,
        #FactInspectionPivot .e-columnsheader .e-headercelldiv,
        #FactInspectionPivot .e-columnsheader th,
        #FactInspectionPivot .e-columnsheader table thead tr th {
          font-weight: normal !important;
        }
        /* Prevent header rotation */
        #FactInspectionPivot .e-headercelldiv span,
        #FactInspectionPivot .e-headercell span {
          transform: none !important;
          writing-mode: horizontal-tb !important;
          display: inline-block !important;
        }
        /* Ensure headers are in single row */
        #FactInspectionPivot .e-gridheader table thead tr {
          display: table-row !important;
        }
        #FactInspectionPivot .e-gridheader table thead th {
          display: table-cell !important;
          vertical-align: middle !important;
        }
        /* Force all header rows to be on same level - merge into single row */
        #FactInspectionPivot .e-gridheader table thead {
          display: table-header-group !important;
          position: relative !important;
        }
        /* Make all header rows appear at the same vertical position */
        #FactInspectionPivot .e-gridheader table thead tr {
          display: table-row !important;
          position: relative !important;
        }
        /* Position all header rows at the same top position */
        #FactInspectionPivot .e-gridheader table thead tr:not(:last-child) {
          position: absolute !important;
          top: 0 !important;
          left: 0 !important;
          right: 0 !important;
          z-index: 1 !important;
        }
        #FactInspectionPivot .e-gridheader table thead tr:last-child {
          position: relative !important;
          z-index: 2 !important;
        }
        /* Ensure column headers (statusName) are horizontal and visible */
        #FactInspectionPivot .e-columnsheader,
        #FactInspectionPivot .e-columnsheader th,
        #FactInspectionPivot .e-columnsheader .e-headercell,
        #FactInspectionPivot .e-columnsheader .e-headercelldiv {
          transform: none !important;
          writing-mode: horizontal-tb !important;
          text-orientation: mixed !important;
          white-space: normal !important;
          height: auto !important;
          line-height: normal !important;
          vertical-align: middle !important;
        }
        /* Force all header rows to have the same height - single row only */
        #FactInspectionPivot .e-gridheader table {
          border-collapse: collapse !important;
        }
        #FactInspectionPivot .e-gridheader table thead {
          height: 55px !important;
          max-height: 55px !important;
          display: table-header-group !important;
        }
        /* Hide all header rows except the last one */
        #FactInspectionPivot .e-gridheader table thead tr:not(:last-child) {
          display: none !important;
          height: 0 !important;
          max-height: 0 !important;
          overflow: hidden !important;
        }
        /* Force the last header row to be exactly 55px */
        #FactInspectionPivot .e-gridheader table thead tr:last-child {
          height: 55px !important;
          min-height: 55px !important;
          max-height: 55px !important;
          line-height: 16px !important;
          display: table-row !important;
        }
        /* Ensure all header cells are on same row level and same height */
        #FactInspectionPivot .e-gridheader table thead tr:last-child th {
          position: relative !important;
          top: auto !important;
          transform: none !important;
          vertical-align: middle !important;
          height: 55px !important;
          min-height: 55px !important;
          max-height: 55px !important;
          padding: 2px 0 !important;
          margin: 0 !important;
          box-sizing: border-box !important;
          text-align: center !important;
          font-weight: normal !important;
        }
        /* Ensure column header cells match value header cell height */
        #FactInspectionPivot .e-columnsheader th,
        #FactInspectionPivot .e-valueheader th,
        #FactInspectionPivot .e-columnsheader table thead tr th,
        #FactInspectionPivot .e-valueheader table thead tr th {
          height: 55px !important;
          min-height: 55px !important;
          max-height: 55px !important;
          box-sizing: border-box !important;
        }
        /* Ensure header cell content displays fully */
        #FactInspectionPivot .e-headercell,
        #FactInspectionPivot .e-headercelldiv {
          height: 100% !important;
          max-height: 50px !important;
          overflow: visible !important;
          line-height: 18px !important;
          display: block !important;
          text-align: center !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        /* Align column header content to center */
        #FactInspectionPivot .e-columnsheader .e-headercell,
        #FactInspectionPivot .e-columnsheader .e-headercelldiv {
          text-align: center !important;
          display: block !important;
          font-weight: normal !important;
        }
        /* Keep value header content centered */
        #FactInspectionPivot .e-valueheader .e-headercell,
        #FactInspectionPivot .e-valueheader .e-headercelldiv {
          text-align: center !important;
          display: block !important;
        }
        /* Style nested elements in header cells */
        #FactInspectionPivot .e-headercell *,
        #FactInspectionPivot .e-headercelldiv * {
          max-height: 50px !important;
          line-height: 16px !important;
          font-size: 9px !important;
        }
        /* Make header text smaller to fit screen */
        #FactInspectionPivot .e-columnsheader th,
        #FactInspectionPivot .e-columnsheader .e-headercell,
        #FactInspectionPivot .e-columnsheader .e-headercelldiv,
        #FactInspectionPivot .e-columnsheader .e-headercell *,
        #FactInspectionPivot .e-columnsheader .e-headercelldiv * {
          font-size: 9px !important;
          line-height: 14px !important;
        }
        /* Set height of row handler cursor to 0 */
        #FactInspectionPivot .e-rhandler.e-rcursor,
        #FactInspectionPivot .e-rhandler,
        #FactInspectionPivot .e-rcursor {
          height: 0 !important;
          min-height: 0 !important;
          max-height: 0 !important;
          display: none !important;
        }
        /* Ensure PivotViewComponent has auto height to fit all rows */
        #FactInspectionPivot {
          height: auto !important;
          min-height: auto !important;
          max-height: none !important;
          margin-right: -40px !important;
        }
        #FactInspectionPivot .e-pivotview {
          height: auto !important;
          min-height: auto !important;
          max-height: none !important;
          overflow: visible !important;
        }
        /* Ensure grid content expands to fit all rows */
        #FactInspectionPivot .e-grid,
        #FactInspectionPivot .e-gridcontent,
        #FactInspectionPivot .e-content {
          height: auto !important;
          min-height: auto !important;
          max-height: none !important;
          overflow: visible !important;
        }
        #FactInspectionPivot .e-gridcontent table,
        #FactInspectionPivot .e-content table {
          height: auto !important;
        }
        /* Set line height of each row to 24px */
        #FactInspectionPivot table tbody tr {
          line-height: 24px !important;
          height: auto !important;
          border-bottom: 1px solid #e0e0e0 !important;
        }
        #FactInspectionPivot table tbody td,
        #FactInspectionPivot .e-gridcell,
        #FactInspectionPivot .e-valuescontent {
          line-height: 24px !important;
          padding: 2px 4px !important;
          border-bottom: 1px solid #e0e0e0 !important;
          border-right: 1px solid #e0e0e0 !important;
          text-align: right !important;
          vertical-align: middle !important;
        }
        /* Align row header cells to left */
        #FactInspectionPivot .e-rowsheader th,
        #FactInspectionPivot .e-rowsheader td,
        #FactInspectionPivot table tbody td.e-rowsheader {
          text-align: left !important;
          font-size: 11px !important;
        }
        /* Make company name text smaller in row headers */
        #FactInspectionPivot .e-rowsheader th *,
        #FactInspectionPivot .e-rowsheader td *,
        #FactInspectionPivot table tbody td.e-rowsheader * {
          font-size: 11px !important;
        }
        /* Ensure column headers have matching padding and alignment */
        #FactInspectionPivot .e-columnsheader th,
        #FactInspectionPivot .e-columnsheader table thead tr th {
          padding: 2px 0 !important;
          margin: 0 !important;
          text-align: center !important;
          vertical-align: middle !important;
          font-weight: normal !important;
        }
        /* Ensure table columns align properly - use fixed layout */
        #FactInspectionPivot table {
          table-layout: fixed !important;
          width: 100% !important;
        }
        #FactInspectionPivot table thead th,
        #FactInspectionPivot table tbody td {
          padding-left: 0 !important;
          padding-right: 0 !important;
          margin: 0 !important;
        }
        /* Match column widths between header and body */
        #FactInspectionPivot .e-gridheader table,
        #FactInspectionPivot .e-content table {
          table-layout: fixed !important;
          width: 100% !important;
        }
        /* Ensure corresponding header and body cells have same width */
        #FactInspectionPivot table thead th:nth-child(1),
        #FactInspectionPivot table tbody td:nth-child(1),
        #FactInspectionPivot .e-rowsheader th:nth-child(1),
        #FactInspectionPivot .e-rowsheader td:nth-child(1),
        #FactInspectionPivot table tbody td.e-rowsheader:nth-child(1),
        #FactInspectionPivot .e-gridcell:nth-child(1),
        #FactInspectionPivot .e-headercell:nth-child(1),
        #FactInspectionPivot .e-gridheader th:nth-child(1),
        #FactInspectionPivot .e-content td:nth-child(1) {
          width: 200px !important;
          min-width: 200px !important;
          max-width: 200px !important;
        }
        #FactInspectionPivot table thead th:not(:nth-child(1)),
        #FactInspectionPivot table tbody td:not(:nth-child(1)) {
          width: 60px !important;
        }
        /* Add border to header row as well */
        #FactInspectionPivot table thead tr {
          border-bottom: 1px solid #e0e0e0 !important;
        }
        #FactInspectionPivot table thead th {
          border-bottom: 1px solid #e0e0e0 !important;
          border-right: 1px solid #e0e0e0 !important;
        }
        /* Remove right border from last column */
        #FactInspectionPivot table tbody td:last-child,
        #FactInspectionPivot table thead th:last-child {
          border-right: none !important;
        }
        /* Rounded borders for dropdown lists */
        #month-dropdown,
        #company-dropdown {
          border-radius: 8px !important;
        }
        #month-dropdown .e-input-group,
        #company-dropdown .e-input-group {
          border-radius: 8px !important;
        }
        #month-dropdown .e-input-group input,
        #company-dropdown .e-input-group input {
          border-radius: 8px !important;
        }
        /* Year dropdown width */
        #year-dropdown {
          width: 100px !important;
          min-width: 100px !important;
          max-width: 100px !important;
        }
        #year-dropdown .e-input-group {
          width: 100px !important;
          min-width: 100px !important;
          max-width: 100px !important;
        }
      `}
    </style>
    <NavBar/>
    {loading && (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )}
    {error && (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">Error loading data: {error}</Typography>
      </Box>
    )}
    {!loading && !error && (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 7, marginBottom: 2 }}>
          <Typography variant="h5" sx={{ marginLeft: 1, marginBottom: 0, fontWeight: 'bold', color: 'navy' }}>
            Inspection Status
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <label style={{ fontWeight: '500', marginRight: '8px', whiteSpace: 'nowrap', fontSize: '14px' }}>Select Year</label>
            <DropDownListComponent
              id="year-dropdown"
              dataSource={availableYears}
              fields={{ text: 'text', value: 'value' }}
              value={selectedYear}
              width="100px"
              placeholder="Select Year"
              change={(e) => setSelectedYear(e.value)}
              popupHeight="200px"
              style={{ width: '100px', }}
            />
            <Button
              variant="outlined"
              onClick={exportToExcel}
              sx={{ marginLeft: 2, marginTop: 0, textTransform: 'none', width: 140 }}
            >
              Export to Excel
            </Button>
          </Box>
        </Box>
        {(!jobStatusData || jobStatusData.length === 0) ? (
          <Box sx={{ marginTop: 4, textAlign: 'center' }}>
            <Typography variant="h2" sx={{ color: 'text.secondary', fontWeight: 'bold' }}>
              No Data Available
            </Typography>
          </Box>
        ) : (
          <>
      <PivotViewComponent
          id="FactInspectionPivot"
          ref={pivotRef}
          width="100%"
          allowExcelExport={true}
          excelExportComplete={excelExportComplete}
          gridSettings={{  
            allowResizing: true,
            showColumnSubTotals: true,
            showColumnGrandTotals: true,
            height: 'auto',
            headerCellInfo: onHeaderCellInfo,
            allowExcelExport: true,
          }}
          dataSourceSettings={{
            dataSource: transformedJobStatusData,
            expandAll: true,         
            showGrandTotals: true,
            showSubTotals: true,
            showRowGrandTotals: true,
            showColumnGrandTotals: true,
            showRowSubTotals: true,
            showColumnSubTotals: true,
            showValueCaption: false,
            valueAxis: 'column',       // values as columns
            rows: [
              { name: 'companyName', caption: 'Company' },
              { 
                name: 'monthName', 
                caption: 'Month',
                sortOrder: 'Ascending'
              },
            ],
            sortSettings: [
              {
                name: 'monthName',
                order: 'Ascending',
                membersOrder: Object.keys(monthOrder).sort((a, b) => monthOrder[a] - monthOrder[b])
              }
            ],
            // no dimension columns – metrics appear as columns
            columns: [ { 
              name: 'statusName', 
              caption: 'Status'
            }],
            values: [ { name: 'count', caption: 'Count', format: 'N0', type: 'sum' }],
            filters: [],
            enableValueSorting: false,
            showHeaderWhenEmpty: false
          }}
        >
          <PivotInject services={[ExcelExport]} />
        </PivotViewComponent>
        {/* Dropdowns for Month and Company - always visible */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 2, marginTop: 0, marginBottom: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', marginLeft: 2, alignItems: 'center', gap: 1 }}>
            <label style={{ fontWeight: '500', marginRight: '8px', whiteSpace: 'nowrap', fontSize: '14px' }}>Month</label>
            <DropDownListComponent
              id="month-dropdown"
              dataSource={availableMonths}
              fields={{ text: 'text', value: 'value' }}
              value={selectedMonth}
              placeholder="Select Month"
              change={(e) => setSelectedMonth(e.value)}
              popupHeight="300px"
              style={{ width: '120px', }}
            />
          </Box>
          {availableCompanies.length > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <label style={{ fontWeight: '500', marginRight: '8px', whiteSpace: 'nowrap', fontSize: '14px' }}>Company</label>
              <DropDownListComponent
                id="company-dropdown"
                dataSource={availableCompanies}
                fields={{ text: 'text', value: 'value' }}
                value={selectedCompany}
                placeholder="Select Company"
                change={(e) => setSelectedCompany(e.value)}
                popupHeight="300px"
                style={{ width: '400px', }}
              />
            </Box>
          )}
        </Box>
        {pieChartData && Array.isArray(pieChartData) && pieChartData.length > 0 && pieChartData.some(item => item.y > 0) ? (
          <Box sx={{ marginTop: 0, paddingTop: 0, width: '100%' }}>
            {/* Pie chart */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', width: '100%', alignItems: 'flex-start', paddingLeft: '60px', paddingRight: '60px' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start' }}>
                <AccumulationChartComponent 
                id='pie-chart2' 
                load={loadChart} 
                width="700px"
                margin={{ left: 80, right: 80, top: 20, bottom: 20 }}
              legendSettings={{ 
                visible: true, 
                toggleVisibility: false, 
                position: 'Right', 
                textWrap: 'Wrap',
                width: '250px',
                paddingLeft: '40px',
                textStyle: {
                  textOverflow: 'none',
                  overflow: 'visible'
                }
              }} 
              enableBorderOnMouseMove={false} 
              selectionMode='Point' 
              tooltip={{ 
                enable: true, 
                format: '<b>${point.x}</b><br>Percentage: <b>${point.y}%</b>', 
                header: "", 
                enableHighlight: true 
              }} 
              height="350px"
            >
              <Inject services={[AccumulationLegend, PieSeries, AccumulationDataLabel, AccumulationTooltip, AccumulationSelection, Selection, ChartAnnotation, AccumulationAnnotation]} />
              <AccumulationSeriesCollectionDirective>
                <AccumulationSeriesDirective 
                  dataSource={pieChartData}
                  xName='x' 
                  yName='y' 
                  innerRadius='50%'
                  radius='70%' 
                  borderRadius={3} 
                  border={{ width: 1, color: '#ffffff' }} 
                  dataLabel={{ 
                    visible: true,
                    position: 'Outside',
                    name: 'label',
                    enableSmartLabels: false,
                    showZero: true,
                    connectorStyle: {
                      length: '30px',
                      type: 'Curve'
                    },
                    font: {
                      size: '9px'
                    }
                  }} 
                />
              </AccumulationSeriesCollectionDirective>
            </AccumulationChartComponent>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ marginTop: 2, paddingTop: 2, width: '100%', textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              <h2><b>No data available</b></h2>
            </Typography>
            </Box>
          )}
          </>
        )}
      </>
    )}
    </>
  );
}
export default InspectionAnalytics;